import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, b2bApi, getToken, setToken, clearToken, pickToken, setOnUnauthorized } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [platform, setPlatform] = useState(null); // /platform/me — roles, org, mode
  const [b2b, setB2b] = useState(null); // /b2b/me — B2B portals, role grants, org licence
  const [initializing, setInitializing] = useState(true);
  const [authError, setAuthError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      if (!mounted.current) return null;
      setUser(me.data);
      // Best-effort — platform/me may 404 or return empty for pure client users
      try {
        const pl = await api.get("/platform/me");
        if (mounted.current) setPlatform(pl.data);
      } catch {
        if (mounted.current) setPlatform(null);
      }
      try {
        const b = await b2bApi.get("/b2b/me");
        if (mounted.current) setB2b(b.data);
      } catch {
        if (mounted.current) setB2b(null);
      }
      return me.data;
    } catch (e) {
      if (mounted.current) {
        setUser(null);
        setPlatform(null);
        setB2b(null);
      }
      throw e;
    }
  }, []);

  // Initial session bootstrap
  useEffect(() => {
    const boot = async () => {
      // CRITICAL: If we're returning from Emergent OAuth callback, skip the /me
      // check — AuthCallback will exchange the session_id and refresh us.
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
        setInitializing(false);
        return;
      }
      if (!getToken()) {
        setInitializing(false);
        return;
      }
      try {
        await fetchMe();
      } catch {
        clearToken();
      } finally {
        if (mounted.current) setInitializing(false);
      }
    };
    boot();
  }, [fetchMe]);

  // Global 401 handling
  useEffect(() => {
    setOnUnauthorized(() => {
      clearToken();
      setUser(null);
      setPlatform(null);
      setB2b(null);
    });
    return () => setOnUnauthorized(null);
  }, []);

  const login = useCallback(
    async ({ email, password, invite_token }) => {
      setAuthError(null);
      const res = await api.post("/auth/login", { email, password, invite_token });
      const token = pickToken(res.data);
      if (!token) throw new Error("No token returned by server.");
      setToken(token);
      await fetchMe();
      return res.data;
    },
    [fetchMe]
  );

  const register = useCallback(
    async ({ email, name, password, invite_token }) => {
      setAuthError(null);
      const res = await api.post("/auth/register", { email, name, password, invite_token });
      const token = pickToken(res.data);
      if (token) {
        setToken(token);
        await fetchMe();
      }
      return res.data;
    },
    [fetchMe]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearToken();
    setUser(null);
    setPlatform(null);
    setB2b(null);
    // Clear access-status cache so next login re-checks.
    try {
      const mod = await import("@/components/auth/ProtectedRoute");
      mod.resetAccessCache?.();
    } catch {}
  }, []);

  const refresh = fetchMe;

  const value = {
    user,
    platform,
    b2b,
    initializing,
    authError,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refresh,
    setPlatform,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

/** Role helpers — tolerant to different backend shapes. */
export const getRoles = (user, platform, b2b) => {
  const roles = new Set();
  if (!user && !platform) return roles;
  const collect = (v) => {
    if (!v) return;
    if (Array.isArray(v)) v.forEach((x) => x && roles.add(String(x).toLowerCase()));
    else if (typeof v === "string") roles.add(v.toLowerCase());
    else if (typeof v === "object") Object.keys(v).filter((k) => v[k]).forEach((k) => roles.add(k.toLowerCase()));
  };
  collect(user?.role);
  collect(user?.roles);
  collect(user?.permissions);
  collect(platform?.role);
  collect(platform?.roles);
  collect(platform?.permissions);
  collect(platform?.portals);
  // The Inner Compass backend exposes platform-level administration as `is_admin`.
  if (user?.is_admin || user?.admin || platform?.is_admin) { roles.add("admin"); roles.add("platform_owner"); }
  collect(b2b?.principal?.extra_roles);
  if (b2b?.portals?.platform_owner) roles.add("platform_owner");
  if (platform?.is_care_professional || platform?.can_access_care) roles.add("care_professional");
  if (platform?.actual_role) roles.add(String(platform.actual_role).toLowerCase());
  if (user?.is_platform_owner || platform?.is_platform_owner) roles.add("platform_owner");
  if (user?.is_super_admin || platform?.is_super_admin) roles.add("super_admin");
  if (user?.is_hr_admin || platform?.is_hr_admin) roles.add("hr_admin");
  if (user?.is_flm_admin || platform?.is_flm_admin) roles.add("flm_admin");
  if (user?.is_therapist || platform?.is_therapist) roles.add("therapist");
  if (user?.is_hr || platform?.is_hr) roles.add("hr");
  return roles;
};

/** Role predicates — narrow, role-name-aware. */
export const isPlatformOwner = (roles) =>
  !!roles && (roles.has("platform_owner") || roles.has("platform-owner") || roles.has("owner"));
export const isSuperAdmin = (roles) =>
  !!roles && (isPlatformOwner(roles) || roles.has("super_admin") || roles.has("superadmin") || roles.has("platform_admin"));
export const isHrAdmin = (roles) =>
  !!roles && (roles.has("hr_admin") || roles.has("hr-admin"));
export const isFlmAdmin = (roles) =>
  !!roles && (roles.has("flm_admin") || roles.has("flm-admin"));
export const isTherapist = (roles) =>
  !!roles && (isPlatformOwner(roles) || roles.has("therapist") || roles.has("clinician") || roles.has("care") || roles.has("care_admin"));
export const isAnyAdmin = (roles) => isPlatformOwner(roles) || isSuperAdmin(roles) || isHrAdmin(roles) || isFlmAdmin(roles);
export const isOrgAdmin = (roles) =>
  !!roles && (roles.has("hr_admin") || roles.has("business_admin") || roles.has("org_admin") || roles.has("organization_admin"));
export const isOccupationalHealth = (roles) => !!roles && roles.has("occupational_health");
export const isCareProfessional = (roles) =>
  !!roles && (roles.has("care_professional") || roles.has("therapist") || roles.has("psychologist") || roles.has("psychiatrist") ||
    roles.has("care_coordinator") || roles.has("care_admin") || roles.has("clinician") || roles.has("care"));

export const canAccess = (roles, portal) => {
  if (!roles) return false;
  // Platform_owner and super_admin bypass all portal restrictions.
  if (isPlatformOwner(roles) || roles.has("super_admin") || roles.has("superadmin") || roles.has("platform_admin")) return true;

  // Business roles = { employee, flm_admin, hr_admin, business_admin, manager, hr, org_admin }.
  const hasBusinessRole = roles.has("flm_admin") || roles.has("hr_admin") ||
    roles.has("business_admin") || roles.has("hr") || roles.has("manager") || roles.has("org_admin");
  // Care roles = { client, therapist, psychologist, psychiatrist, care_coordinator, care_admin, clinician }.
  const hasCareRole = roles.has("client") || roles.has("therapist") || roles.has("psychologist") ||
    roles.has("psychiatrist") || roles.has("care_coordinator") || roles.has("care_admin") ||
    roles.has("care") || roles.has("clinician") || roles.has("care_professional");

  switch (portal) {
    case "admin":
    case "owner":
      return false; // reachable only via platform_owner (handled above)
    case "oh":
      return roles.has("occupational_health");
    case "hr":
    case "business":
      // Organisation administration: HR/org admins and FLMs (limited views).
      return hasBusinessRole && !roles.has("employee_only");
    case "care":
      // Care roles can access the Care portal; a user may also hold a business role (combined accounts).
      return hasCareRole;
    case "client":
    default:
      return true; // everyone gets a client space (their own Inner Compass)
  }
};
